import React, { useState, useEffect } from 'react';
import { Box, Button, TextField } from '@mui/material';
import {
  Search as SearchIcon,
  BorderColor as BulkEditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FilterDrawer from './FilterDrawer';
import SelectField from '../inputs/SelectField';

const DIAMOND_STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'sold', label: 'Sold' },
  { value: 'onTheWay', label: 'On the Way' },
  { value: 'notForSell', label: 'Not for sell' },
  { value: 'deleted', label: 'Deleted' },
];

const DIAMOND_GRADE_OPTIONS = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'];

export const DEFAULT_CERTIFIED_FILTERS = {
  status: ['available'],
  shape: '',
  color: [],
  fancyIntensity: [],
  fancyHue: [],
  fancyOvertone: [],
  clarity: [],
  minCarat: '',
  maxCarat: '',
  sku: '',
  certificateNumber: '',
  shipmentId: '',
  shipmentItemID: '',
  cut: '',
  polish: '',
  symmetry: '',
};

export const DEFAULT_CERTIFIED_ADVANCED_FILTERS = {
  minRatio: '',
  maxRatio: '',
  minLength: '',
  maxLength: '',
  minWidth: '',
  maxWidth: '',
};

export function CertifiedFilterFields({
  filters,
  advancedFilters,
  filterOptions,
  isInternal,
  includeAllShapeOption = true,
  colorCategory = 'standard',
  hideFancyHueFilter = false,
  onFilterChange,
  onAdvancedFilterChange,
}) {
  const isFancy = colorCategory === 'fancy';
  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ width: '100%' }}>
      {isInternal && (
        <SelectField
          label="Status"
          multiple
          fullWidth
          value={Array.isArray(filters.status) ? filters.status : []}
          onChange={(next) => onFilterChange('status', next)}
          options={DIAMOND_STATUS_OPTIONS}
          emptyLabel="All"
        />
      )}
      {isInternal && <TextField size="small" label="SKU" value={filters.sku} onChange={(e) => onFilterChange('sku', e.target.value)} fullWidth />}
      {isInternal && <TextField size="small" label="Shipment ID" value={filters.shipmentId} onChange={(e) => onFilterChange('shipmentId', e.target.value)} placeholder="Contains…" fullWidth />}
      {isInternal && <TextField size="small" label="Shipment Item ID" value={filters.shipmentItemID} onChange={(e) => onFilterChange('shipmentItemID', e.target.value)} placeholder="Contains…" fullWidth />}
      <SelectField
        label="Shape"
        fullWidth
        value={filters.shape}
        onChange={(next) => onFilterChange('shape', next)}
        options={(filterOptions.shapes || []).map((shape) => {
          const s = String(shape).toUpperCase();
          return { value: s, label: s };
        })}
        emptyLabel="All"
        includeEmptyOption={includeAllShapeOption}
      />
      {isFancy ? (
        <>
          <SelectField
            label="Intensity"
            multiple
            value={filters.fancyIntensity}
            onChange={(next) => onFilterChange('fancyIntensity', next)}
            options={filterOptions.fancyIntensities || []}
            emptyLabel="All"
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'flex-start' }}>
            {!hideFancyHueFilter && (
              <SelectField
                label="Hue"
                multiple
                value={filters.fancyHue}
                onChange={(next) => onFilterChange('fancyHue', next)}
                options={filterOptions.fancyHues || []}
                emptyLabel="All"
                sx={{ flex: 1, minWidth: 0 }}
              />
            )}
            <SelectField
              label="Overtone"
              multiple
              value={filters.fancyOvertone}
              onChange={(next) => onFilterChange('fancyOvertone', next)}
              options={filterOptions.fancyOvertones || []}
              emptyLabel="All"
              sx={hideFancyHueFilter ? { width: '100%' } : { flex: 1, minWidth: 0 }}
            />
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'flex-start' }}>
          <SelectField
            label="Color"
            multiple
            value={filters.color}
            onChange={(next) => onFilterChange('color', next)}
            options={filterOptions.colors || []}
            emptyLabel="All"
            sx={{ flex: 1, minWidth: 0 }}
          />
          <SelectField
            label="Clarity"
            multiple
            value={filters.clarity}
            onChange={(next) => onFilterChange('clarity', next)}
            options={filterOptions.clarities || []}
            emptyLabel="All"
            sx={{ flex: 1, minWidth: 0 }}
          />
        </Box>
      )}
      {isFancy && (
        <SelectField
          label="Clarity"
          multiple
          fullWidth
          value={filters.clarity}
          onChange={(next) => onFilterChange('clarity', next)}
          options={filterOptions.clarities || []}
          emptyLabel="All"
        />
      )}
      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
        <TextField size="small" label="Min Carat" type="number" value={filters.minCarat} onChange={(e) => onFilterChange('minCarat', e.target.value)} sx={{ flex: 1, minWidth: 0 }} />
        <TextField size="small" label="Max Carat" type="number" value={filters.maxCarat} onChange={(e) => onFilterChange('maxCarat', e.target.value)} sx={{ flex: 1, minWidth: 0 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75, width: '100%', alignItems: 'flex-start' }}>
        <SelectField
          label="Cut"
          value={filters.cut}
          onChange={(next) => onFilterChange('cut', next)}
          options={filterOptions.cuts || []}
          emptyLabel="All"
          includeEmptyOption
          sx={{ flex: 1, minWidth: 0 }}
        />
        <SelectField
          label="Polish"
          value={filters.polish}
          onChange={(next) => onFilterChange('polish', next)}
          options={DIAMOND_GRADE_OPTIONS}
          emptyLabel="All"
          includeEmptyOption
          sx={{ flex: 1, minWidth: 0 }}
        />
        <SelectField
          label="Symmetry"
          value={filters.symmetry}
          onChange={(next) => onFilterChange('symmetry', next)}
          options={DIAMOND_GRADE_OPTIONS}
          emptyLabel="All"
          includeEmptyOption
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
        <TextField size="small" label="Min Ratio" type="number" value={advancedFilters.minRatio} onChange={(e) => onAdvancedFilterChange('minRatio', e.target.value)} inputProps={{ step: 0.01, min: 0 }} sx={{ flex: 1, minWidth: 0 }} />
        <TextField size="small" label="Max Ratio" type="number" value={advancedFilters.maxRatio} onChange={(e) => onAdvancedFilterChange('maxRatio', e.target.value)} inputProps={{ step: 0.01, min: 0 }} sx={{ flex: 1, minWidth: 0 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
        <TextField size="small" label="Min Length (mm)" type="number" value={advancedFilters.minLength} onChange={(e) => onAdvancedFilterChange('minLength', e.target.value)} inputProps={{ step: 0.1, min: 0 }} sx={{ flex: 1, minWidth: 0 }} />
        <TextField size="small" label="Max Length (mm)" type="number" value={advancedFilters.maxLength} onChange={(e) => onAdvancedFilterChange('maxLength', e.target.value)} inputProps={{ step: 0.1, min: 0 }} sx={{ flex: 1, minWidth: 0 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
        <TextField size="small" label="Min Width (mm)" type="number" value={advancedFilters.minWidth} onChange={(e) => onAdvancedFilterChange('minWidth', e.target.value)} inputProps={{ step: 0.1, min: 0 }} sx={{ flex: 1, minWidth: 0 }} />
        <TextField size="small" label="Max Width (mm)" type="number" value={advancedFilters.maxWidth} onChange={(e) => onAdvancedFilterChange('maxWidth', e.target.value)} inputProps={{ step: 0.1, min: 0 }} sx={{ flex: 1, minWidth: 0 }} />
      </Box>
    </Box>
  );
}

/**
 * Filter drawer for certified diamonds. Keeps draft filter state local so typing
 * does not re-render the parent inventory page.
 */
export default function CertifiedFilterDrawer({
  open,
  onClose,
  appliedFilters,
  appliedAdvancedFilters,
  filterOptions,
  isInternal,
  isManager,
  onSearch,
  onClearAll,
  onBulkEdit,
  includeAllShapeOption = true,
  defaultShape = '',
  colorCategory = 'standard',
  hideFancyHueFilter = false,
}) {
  const { t } = useTranslation();
  const [draftFilters, setDraftFilters] = useState(DEFAULT_CERTIFIED_FILTERS);
  const [draftAdvancedFilters, setDraftAdvancedFilters] = useState(DEFAULT_CERTIFIED_ADVANCED_FILTERS);

  useEffect(() => {
    if (!open) return;
    setDraftFilters(appliedFilters);
    setDraftAdvancedFilters(appliedAdvancedFilters);
    // Sync draft only when the drawer opens; applied filters are read at open time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFilterChange = (field, value) => {
    setDraftFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdvancedFilterChange = (field, value) => {
    setDraftAdvancedFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch(draftFilters, draftAdvancedFilters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters = includeAllShapeOption
      ? DEFAULT_CERTIFIED_FILTERS
      : { ...DEFAULT_CERTIFIED_FILTERS, shape: defaultShape };
    setDraftFilters(clearedFilters);
    setDraftAdvancedFilters(DEFAULT_CERTIFIED_ADVANCED_FILTERS);
    onClearAll();
  };

  return (
    <FilterDrawer
      open={open}
      onClose={onClose}
      title={t('inventory.filters')}
      formProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
      actions={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button type="submit" variant="contained" color="primary" startIcon={<SearchIcon />} size="medium" sx={{ flex: 1, minWidth: 0 }}>
              {t('inventory.search')}
            </Button>
            {isInternal && isManager && (
              <Button
                type="button"
                variant="outlined"
                size="medium"
                startIcon={<BulkEditIcon />}
                onClick={() => {
                  onClose();
                  onBulkEdit();
                }}
                sx={{ flex: 1, minWidth: 0 }}
              >
                {t('inventory.bulkEdit')}
              </Button>
            )}
          </Box>
          <Button type="button" variant="outlined" color="inherit" size="medium" fullWidth onClick={handleClear}>
            {t('inventory.clearFilters')}
          </Button>
        </Box>
      }
    >
      <CertifiedFilterFields
        filters={draftFilters}
        advancedFilters={draftAdvancedFilters}
        filterOptions={filterOptions}
        isInternal={isInternal}
        includeAllShapeOption={includeAllShapeOption}
        colorCategory={colorCategory}
        hideFancyHueFilter={hideFancyHueFilter}
        onFilterChange={handleFilterChange}
        onAdvancedFilterChange={handleAdvancedFilterChange}
      />
    </FilterDrawer>
  );
}
